import type {
  SearchCalendarEvent,
  SearchCalendarEventIconName,
  SearchCalendarEventLocation,
  SearchCalendarEventType,
} from 'constants/searchCalendarEvents'
import {
  SEARCH_CALENDAR_EVENT_DEFAULT_COLOR,
  SEARCH_CALENDAR_EVENT_FALLBACK_ICON,
  SEARCH_CALENDAR_EVENT_ICON_NAMES,
  SEARCH_CALENDAR_EVENT_TYPES,
} from 'constants/searchCalendarEvents'

/** Raw row from `GET /api/recommendation/search_calendar_events`. */
export interface ApiSearchCalendarEvent {
  event_id?: string
  title?: string
  short_description?: string | null
  start_date?: string
  end_date?: string
  event_types?: Array<string> | null
  icon?: string | null
  color?: string | null
  rank?: number | null
  is_manually_selected?: boolean | null
  location?: { place_id?: string | null, lat?: number | null, lng?: number | null } | null
  search_place_ids?: Array<string> | null
}

const UNKNOWN_EVENT_TYPE: SearchCalendarEventType = 'UNKNOWN'

const KNOWN_EVENT_TYPES = new Set<string>(SEARCH_CALENDAR_EVENT_TYPES)
const KNOWN_ICON_NAMES = new Set<string>(SEARCH_CALENDAR_EVENT_ICON_NAMES)

/**
 * Feed types this build does not know collapse to `UNKNOWN` rather than leaking
 * an unmapped string into the union. Order is preserved because the first entry
 * is the primary type.
 */
function mapEventTypes(eventTypes?: Array<string> | null): ReadonlyArray<SearchCalendarEventType> {
  const mapped = new Set<SearchCalendarEventType>()
  for (const eventType of eventTypes ?? []) {
    mapped.add(KNOWN_EVENT_TYPES.has(eventType) ? eventType as SearchCalendarEventType : UNKNOWN_EVENT_TYPE)
  }

  return mapped.size ? [...mapped] : [UNKNOWN_EVENT_TYPE]
}

/**
 * An icon name outside the union would reach `CalendarEventOverlayIcon`'s lookup
 * and render `undefined` as a component, so an unrecognised name is substituted
 * here rather than at the render boundary.
 */
function mapIcon(icon?: string | null): SearchCalendarEventIconName {
  if (icon && KNOWN_ICON_NAMES.has(icon)) return icon as SearchCalendarEventIconName

  return SEARCH_CALENDAR_EVENT_FALLBACK_ICON
}

/**
 * The feed sends `place_id: null` for events with no LE place of their own, and
 * coordinates instead. Absent keys rather than explicit nulls keep the shape
 * matching what `SearchCalendarEventLocation` documents.
 */
function mapLocation(location: ApiSearchCalendarEvent['location']): SearchCalendarEventLocation {
  const mapped: SearchCalendarEventLocation = {}
  if (location?.place_id) mapped.placeId = location.place_id
  if (typeof location?.lat === 'number') mapped.lat = location.lat
  if (typeof location?.lng === 'number') mapped.lng = location.lng

  return mapped
}

/**
 * Maps one feed row, or returns `undefined` for a row the calendar cannot use.
 *
 * Every helper in `lib/search/searchCalendarEvents` does ISO string arithmetic
 * on `startDate`/`endDate`, so dropping a malformed row here is the difference
 * between one missing event and a broken calendar.
 */
export function searchCalendarEventMap(event: ApiSearchCalendarEvent): SearchCalendarEvent | undefined {
  const { event_id: id, title, start_date: startDate, end_date: endDate } = event
  if (!id || !title || !startDate || !endDate || endDate < startDate) return undefined

  return {
    id,
    title,
    shortDescription: event.short_description || undefined,
    startDate,
    endDate,
    eventTypes: mapEventTypes(event.event_types),
    icon: mapIcon(event.icon),
    color: event.color || SEARCH_CALENDAR_EVENT_DEFAULT_COLOR,
    rank: event.rank ?? undefined,
    isManuallySelected: !!event.is_manually_selected,
    location: mapLocation(event.location),
    searchPlaceIds: event.search_place_ids ?? [],
  }
}
